import React, { useState } from 'react';
import { StatusPill } from '../../components/SubscriptionStatus';
import { useEntitlement, useInvoices, useUpcomingInvoice } from '../../hooks/useApi';
import { InvoiceHistoryItem, UpcomingInvoice } from '../../types';
import { Panel } from './Panel';
import { Skeleton } from './Skeleton';
import { StatTile, StatTileRow } from './StatTile';
import { cx, FOCUS_RING } from './styles';

/**
 * The three panels that answer "what am I being charged".
 *
 * They live here rather than on one screen because both Profile and Billing
 * need them and a copy on each would drift — the two pages would end up
 * disagreeing about a number, which is the one thing a billing surface cannot
 * do. Profile shows them beside the identity card; Billing shows them beneath
 * the plan chooser, where someone comparing tiers wants to see what they are
 * paying now.
 *
 * Every one is gated on holding a tier: an account with no FlashTools
 * subscription has no FlashTools bill to preview, even if it is still billed
 * for Dice numbers.
 */


function formatUsd(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatMoney(amount: number, currency: string) {
  if ((currency || '').toLowerCase() === 'usd') return formatUsd(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(amount);
}

function formatUnixDate(ts: number | null) {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString();
}



/**
 * The three numbers this page is opened for, above the panels that explain
 * them. Both hooks are already mounted by the sections below and both are
 * plain `useQuery`s on stable keys, so this renders from the same two responses
 * rather than fetching anything of its own.
 *
 * Gated on `entitlement.tier` for the same reason `UpcomingInvoiceSection` is:
 * an account with no FlashTools tier has no FlashTools bill to preview, even
 * though it may still be billed for Dice numbers — that activity is itemised in
 * `Usage detail`, which is deliberately ungated.
 */
export function BillingSummaryStrip() {
  const { data: entitlement } = useEntitlement();
  const { data, isLoading, error } = useUpcomingInvoice();
  if (!entitlement?.tier) return null;

  const invoice: UpcomingInvoice | null = data?.invoice ?? null;
  const periodEnd = entitlement.current_period_end
    ? new Date(entitlement.current_period_end).toLocaleDateString()
    : null;

  // The charge date comes from Stripe's own next attempt when we have a
  // preview, and falls back to the entitlement's period end when we don't —
  // never invented, and labelled differently in each case.
  const chargeDate = invoice?.next_payment_attempt
    ? formatUnixDate(invoice.next_payment_attempt)
    : periodEnd ?? '—';

  return (
    <StatTileRow cols={3}>
      <StatTile
        label="Plan"
        value={entitlement.tier.charAt(0).toUpperCase() + entitlement.tier.slice(1)}
        subtitle={entitlement.comped ? 'Admin access' : entitlement.status ?? undefined}
        color="blue"
      />
      <StatTile
        label="Next charge"
        value={isLoading ? '…' : error || !invoice ? '—' : formatMoney(invoice.amount_due, invoice.currency)}
        subtitle={
          isLoading
            ? undefined
            : error || !invoice
              ? 'No preview available'
              : 'Estimate — updates as you use FlashTools'
        }
      />
      <StatTile
        label={entitlement.cancel_at_period_end ? 'Access ends' : 'Charges on'}
        value={entitlement.cancel_at_period_end ? periodEnd ?? '—' : chargeDate}
        subtitle={
          entitlement.cancel_at_period_end
            ? 'Cancellation scheduled'
            : invoice?.next_payment_attempt
              ? undefined
              : 'End of current period'
        }
        color={entitlement.cancel_at_period_end ? 'amber' : 'default'}
      />
    </StatTileRow>
  );
}



export function UpcomingInvoiceSection() {
  const { data: entitlement } = useEntitlement();
  const { data, isLoading, error } = useUpcomingInvoice();
  if (!entitlement?.tier) return null;
  const invoice: UpcomingInvoice | null = data?.invoice ?? null;

  return (
    <Panel
      title="Upcoming invoice"
      hint={
        invoice?.next_payment_attempt
          ? `Charges on ${formatUnixDate(invoice.next_payment_attempt)}`
          : undefined
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-3.5 w-28" />
        </div>
      ) : error || !invoice ? (
        <p className="text-sm text-white/60">No upcoming invoice preview available.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm text-white/60">Period</span>
            <span className="text-sm text-white tabular-nums">
              {formatUnixDate(invoice.period_start)} – {formatUnixDate(invoice.period_end)}
            </span>
          </div>
          <div className="divide-y divide-white/5 rounded-lg border border-white/10">
            {invoice.lines.length === 0 && (
              <div className="px-4 py-3 text-sm text-white/60">No line items yet.</div>
            )}
            {invoice.lines.map((line, i) => (
              <div key={i} className="flex items-start justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="break-words text-sm text-white">{line.description}</div>
                  {/* quantity and the line's own period arrive in the payload and
                      were being dropped. On a consolidated bill the quantity is
                      the whole explanation of a line — "37 Dice accounts" is the
                      answer to why the total moved. */}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-white/40">
                    {line.quantity !== null && line.quantity > 1 && (
                      <span className="tabular-nums">Qty {line.quantity}</span>
                    )}
                    {line.period_start && line.period_end && (
                      <span className="tabular-nums">
                        {formatUnixDate(line.period_start)} – {formatUnixDate(line.period_end)}
                      </span>
                    )}
                    {line.proration && <span>Proration</span>}
                  </div>
                </div>
                <div className="whitespace-nowrap text-sm text-white tabular-nums">
                  {formatMoney(line.amount, invoice.currency)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between pt-1">
            <span className="text-sm text-white/60">Subtotal</span>
            <span className="text-sm text-white tabular-nums">
              {formatMoney(invoice.subtotal, invoice.currency)}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-base font-semibold text-white">Total due</span>
            <span className="text-base font-semibold text-white tabular-nums">
              {formatMoney(invoice.amount_due, invoice.currency)}
            </span>
          </div>
        </div>
      )}
    </Panel>
  );
}



/** `lg`+ aligned columns, stacked cards below — the DataList contract (§4.3). */
const INVOICE_COLS = 'lg:grid lg:grid-cols-12 lg:items-center lg:gap-3';

/**
 * Disclosure is a view preference, so it outlives the session. Open by default.
 * The key keeps its original `profile.` name on purpose — renaming it would
 * silently reset the preference for everyone who already set it.
 */
const INVOICES_OPEN_KEY = 'flashtools.profile.invoicesOpen';

const readInvoicesOpen = (): boolean => {
  try {
    return window.localStorage.getItem(INVOICES_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
};

export function InvoiceHistorySection() {
  const { data: entitlement } = useEntitlement();
  const { data, isLoading } = useInvoices(12);
  const [open, setOpen] = useState(readInvoicesOpen);
  if (!entitlement?.tier && !entitlement?.comped) return null;
  const invoices: InvoiceHistoryItem[] = data?.invoices ?? [];

  const handleToggle = (next: boolean) => {
    setOpen(next);
    try {
      window.localStorage.setItem(INVOICES_OPEN_KEY, next ? '1' : '0');
    } catch {
      /* private mode — the preference just doesn't persist */
    }
  };

  return (
    <Panel
      title="Billing history"
      hint={
        isLoading
          ? undefined
          : invoices.length === 0
            ? 'No invoices yet'
            : `${invoices.length} most recent ${invoices.length === 1 ? 'invoice' : 'invoices'}`
      }
      collapsible
      defaultOpen={open}
      onToggle={handleToggle}
      padding="none"
      bodyClassName="p-4 lg:p-0"
    >
      {isLoading ? (
        <div className="space-y-3 lg:p-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-white/60 lg:p-5">
          No invoices yet — your first invoice will show here after your next billing cycle.
        </p>
      ) : (
        <div>
          <div
            aria-hidden="true"
            className={cx(
              'hidden border-b border-white/[0.06] px-4 py-2.5 text-[11px] uppercase tracking-wider text-white/35',
              INVOICE_COLS,
            )}
          >
            <div className="col-span-4">Invoice</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-2 text-right">Links</div>
          </div>
          <div className="space-y-3 lg:space-y-0">
            {invoices.map(inv => (
              <div
                key={inv.id}
                className={cx(
                  'rounded-xl border border-white/[0.06] bg-white/[0.02] p-4',
                  'lg:rounded-none lg:border-0 lg:border-b lg:border-white/[0.05] lg:bg-transparent lg:px-4 lg:py-3',
                  INVOICE_COLS,
                )}
              >
                <div className="min-w-0 lg:col-span-4">
                  <span
                    title={inv.number || inv.id}
                    className="block truncate text-base text-white lg:text-sm"
                  >
                    {inv.number || inv.id}
                  </span>
                </div>
                <div className="mt-1 text-sm text-white/50 lg:col-span-2 lg:mt-0 lg:text-white/70">
                  {formatUnixDate(inv.created)}
                </div>
                <div className="mt-2 lg:col-span-2 lg:mt-0">
                  <StatusPill status={inv.status} />
                </div>
                <div className="mt-2 text-sm text-white tabular-nums lg:col-span-2 lg:mt-0 lg:text-right">
                  {formatMoney(inv.total, inv.currency)}
                </div>
                <div className="mt-3 flex items-center gap-4 border-t border-white/[0.05] pt-3 lg:col-span-2 lg:mt-0 lg:justify-end lg:border-0 lg:pt-0">
                  {inv.invoice_pdf && (
                    <a
                      href={inv.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cx('rounded text-xs text-[#3b82f6] hover:text-[#60a5fa]', FOCUS_RING)}
                    >
                      PDF
                    </a>
                  )}
                  {inv.hosted_invoice_url && (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cx('rounded text-xs text-[#3b82f6] hover:text-[#60a5fa]', FOCUS_RING)}
                    >
                      View
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}


